import { DynamicIcon } from '@/components/dynamic-icon';
import RewardsDialogBase, {
    RewardsDialogBaseProps,
} from '@/components/rewards-dialog-base';
import { MaxColumnsContainer } from '@/components/ui/layout';
import useAppConfig from '@/hooks/use-app-config';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useProvidedProgramContextWithWorkflow } from '@/hooks/use-provided-program-context/with-workflow';
import commonMessages from '@/lang/messages/common';
import { SimpleDialogTitle } from '@/libs/material/simple-dialog/title';
import { CH_CDN_URL } from '@/utils/constants';
import { getProgramId } from '@/utils/programs';
import { convertBenefitLogoToSvg } from '@/utils/rewards';
import { DisplayedClaimReward } from '@/utils/rewards/types';
import { parseMarkdownLinks } from '@/utils/string';
import { EnumButtonName, EnumDialogName } from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_WIDTH } from '@calm/evexia-design-tokens';
import { RewardRewardsDetailsResponse } from '@calm/evexia-web-sdk';
import {
    Box,
    Button,
    Grid,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { SimpleDialogProps } from '@rhg/libs/material/simple-dialog';
import { DialogButton } from '@rhg/libs/material/simple-dialog/types';
import Image from '@rhg/libs/next/image';
import { createContext, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';

export const DEFAULT_TIMEOUT = 2000;
export type JoinDialogButton = Omit<DialogButton, 'onClick'>;

export interface JoinDialogState {
    joinDialog: (options: JoinDialogOptions) => void;
    isOpen: boolean;
}
export const JoinDialogContext = createContext<JoinDialogState>(null!);

export interface JoinDialogOptions {
    trackDialog?: EnumDialogName;
    title?: string;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<
        Omit<SimpleDialogProps, 'iconContainerOverride'>
    >;
    onPlayClick: () => void;
    rewardsDetails?: RewardRewardsDetailsResponse;
}

const styleIcon = {
    background: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.gradient.iconPrimary
            : theme.palette.info.main,
};
const styleRewardItem = {
    display: 'flex',
    alignItems: 'center',
};
const styleSubtitle = { mb: 4 };
const styleLegalText = {
    display: 'inline-block',
};
const styleLegalLink = {
    '& a': {
        color: (theme: Theme) =>
            `${theme.palette.mode === 'light' ? 'primary.main' : 'inherit'}`,
        textDecoration: (theme: Theme) =>
            `${theme.palette.mode === 'light' ? 'initial' : 'underline'}`,
    },
};

const LegalText = ({ legalText }: { legalText: string }) => {
    return (
        <Box sx={styleLegalText}>
            {parseMarkdownLinks(legalText).map((node, index) => {
                if (node.type === 'text') {
                    return (
                        <Typography
                            key={index}
                            variant="caption"
                            component="span"
                            color="text.body"
                        >
                            {node.text}
                        </Typography>
                    );
                } else if (node.type === 'link') {
                    return (
                        <Typography
                            key={index}
                            variant="caption"
                            component="span"
                            color="text.body"
                            sx={styleLegalLink}
                        >
                            <a
                                href={node.href}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {node.text}
                            </a>
                        </Typography>
                    );
                }
                return null;
            })}
        </Box>
    );
};

interface Props {
    reward: DisplayedClaimReward;
    buttonOverride?: ReactNode;
    onClose: () => void;
}

export const RewardDetailsDialog = ({
    reward,
    onClose,
    buttonOverride,
    ...props
}: Props & Partial<RewardsDialogBaseProps>) => {
    const { formatMessage } = useIntl();
    const { NEXT_PUBLIC_LINK_REWARDS_ICONS_BASE_URL: baseUrl } = useAppConfig();
    const isMobile = useIsMobile();
    const { program } = useProvidedProgramContextWithWorkflow();

    const handleOkClick = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <RewardsDialogBase
            isOpen
            onClose={onClose}
            trackDialog={EnumDialogName.RewardSelectionDetailsDialog}
            trackProps={{
                RewardName: reward.affiliatePartner,
                ProgramId: program ? getProgramId(program) : undefined,
                ProgramName: program?.title,
            }}
            trackCloseButton={EnumButtonName.RewardDetailsClose}
            dialogButton={
                buttonOverride ? (
                    buttonOverride
                ) : (
                    <Button
                        onClick={handleOkClick}
                        variant="contained"
                        trackButton={EnumButtonName.RewardDetailsOk}
                        size="large"
                        fullWidth
                    >
                        {formatMessage(commonMessages['common.ok'])}
                    </Button>
                )
            }
            dialogContent={
                <MaxColumnsContainer columns={6} gap={4}>
                    <Image
                        src={`${baseUrl ?? CH_CDN_URL}${convertBenefitLogoToSvg(
                            reward.logo.url
                        )}`}
                        alt={reward.logo.altText}
                        // When using an SVG we don't benefit from next/image optimizations
                        unoptimized
                        width={
                            isMobile
                                ? DS_WIDTH.IMAGE_SMALL
                                : DS_WIDTH.IMAGE_MEDIUM
                        }
                        height={
                            isMobile
                                ? DS_HEIGHT.IMAGE_SMALL
                                : DS_HEIGHT.IMAGE_MEDIUM
                        }
                        style={{
                            alignSelf: 'center',
                            justifySelf: 'center',
                            marginBottom: 32,
                        }}
                    />
                    <div>
                        <SimpleDialogTitle title={reward.title} />
                    </div>
                    {!!reward.subtitle && (
                        <Typography
                            variant="body1"
                            color="text.sub"
                            textAlign="center"
                            sx={styleSubtitle}
                        >
                            {reward.subtitle}
                        </Typography>
                    )}
                    {reward.benefits.map((benefit) => (
                        <Grid
                            container
                            key={`${benefit.title}`}
                            gap={2}
                            alignContent="center"
                            wrap="nowrap"
                        >
                            {benefit.logo && (
                                <Grid item>
                                    {
                                        <DynamicIcon
                                            src={`${baseUrl}${convertBenefitLogoToSvg(
                                                benefit.logo.url
                                            )}`}
                                            size={35}
                                            sx={styleIcon}
                                        />
                                    }
                                </Grid>
                            )}
                            <Grid item sx={styleRewardItem}>
                                <Typography
                                    variant="h6"
                                    component="p"
                                    color="text.body"
                                >
                                    {benefit.title}
                                </Typography>
                            </Grid>
                        </Grid>
                    ))}
                    {!!(
                        reward?.legal?.text &&
                        reward.legal.format === 'markdown'
                    ) && <LegalText legalText={reward.legal.text} />}
                </MaxColumnsContainer>
            }
            {...props}
        />
    );
};
